import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import configureStore from "./store/reducers/store";
import App from "./App";
import "./i18n";
import reportWebVitals from "./reportWebVitals";
import axiosInstance from "./translator";
import { localStorageHelper } from "./utils";
import { statusCode } from "./constants/apis/apiStatus";
import { toastNotification } from "./utils/toast";
import { authTypes, toasterFlags } from "./constants/componentConstants";
import { userAuthenticationAPI as auth } from "./constants/apis/apiEndpoints";
import { getEnv, getLogoutData, delay } from "./utils/genericUtils";
import { logoutUser } from "./store/actions";
import OidcAuthService from "./config/OidcAuthService";
import { directLoginStorage } from "./utils/storageHelper";
import "./index.scss";

const store = configureStore();
let userType = localStorageHelper.get("auth_type");
let refreshingToken = null;
let countLogout = false;
let count = 0;
const fetchRefreshToken = () => {
  if (userType === authTypes?.localUser) {
    return axiosInstance.post(`${auth.jwtTokenEndpoint}`, {
      refresh: localStorageHelper.get("refreshToken"),
    });
  } else {
    return axiosInstance.post(`${auth.refreshTokenEndpoint}`, {
      sessionid: localStorageHelper.get("sessionId"),
    });
  }
};

const logoutFunc = () => {
  if (localStorageHelper.get("auth_type") === authTypes.azuerAD || localStorageHelper.get("wsm_auth_type") === authTypes.azuerAD) {
    const logout = getLogoutData();
    store.dispatch(logoutUser(logout));
  } else {
    let tenantUrl = localStorageHelper.get("tenantUrl");
    let wsmTenantUrl = localStorageHelper.get("wsmTenantUrl");

    localStorageHelper.clear();
    sessionStorage.clear();
    directLoginStorage.clear();

    tenantUrl && window?.location?.replace(`${getEnv()}${tenantUrl}`);
    wsmTenantUrl && window?.location?.replace(`${wsmTenantUrl}`);
  }
};

const logOutHandler = (errorData) => {
  countLogout = true;

  //for displaying error msg
  if (errorData?.message === "Session Expired") {
    toastNotification(toasterFlags.ERROR, "Session has expired. Please login again.");
  } else {
    toastNotification(toasterFlags.ERROR, errorData?.message ? errorData?.message : errorData?.detail);
  }

  setTimeout(() => {
    logoutFunc();
  }, 1000);
};

const logoutOnSessionExpired = (errorData) => {
  //for displaying error msg
  setTimeout(() => {
    if (!JSON.parse(localStorageHelper.get("isTokenRefreshed"))) {
      countLogout = true;
      toastNotification(toasterFlags.ERROR, "Session has expired. Please login again.");
      logoutFunc();
    }
  }, 1000);
};

const resetRefreshedTokens = () => {
  localStorageHelper.set("isTokenRefreshed", true);
  setTimeout(() => {
    localStorageHelper.set("isTokenRefreshed", false);
    count = 0;
  }, 30000);
};
const getNewAccessToken = async () => {
  let url = localStorageHelper.get("auth_url") || localStorageHelper.get("wsm_auth_url");
  let tenantName = localStorageHelper.get("tenant_name");
  let oidc = new OidcAuthService(url, tenantName);
  return oidc.refreshAccessToken();
};

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  </React.StrictMode>,
  document.getElementById("root")
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const status = error?.status || error?.response?.status;
    const errorData = error?.response?.data;
    const requestData = error?.config || {};

    // Code for generic error handling
    if (status === statusCode.BadRequest400 && errorData?.message === "Session Expired") {
      // maintain a flag to call logout api only once
      if (!countLogout) {
        //if session is expired then making logout api call
        logOutHandler(errorData);
      } else {
        return;
      }
    }

    //Code for checking invalid access token
    if (
      (errorData?.detail === "Token is expired" ||
        errorData?.detail === "Invalid or Expired token" ||
        errorData?.detail === "Signature has expired") &&
      !requestData?._retry
    ) {
      requestData._retry = true;
      try {
        if (count === 0) {
          count = count + 1;
          if (localStorageHelper.get("auth_type") === authTypes.azuerAD || localStorageHelper.get("wsm_auth_type") === authTypes.azuerAD) {
            // fetch refresh token for AAD user
            refreshingToken = refreshingToken ? refreshingToken : fetchRefreshToken();
            let response = await refreshingToken;
            refreshingToken = null;

            //Set new tokens for AAD
            if (userType === authTypes?.localUser) {
              if (response?.data?.access) {
                localStorageHelper.set("accessToken", response?.data?.access);
                localStorageHelper.set("authToken", response?.data?.access);
                resetRefreshedTokens();
              } else {
                logoutOnSessionExpired();
              }
            } else {
              let accessToken, token;
              ({ accessToken, token } = response?.data?.success);

              //check token for AAD login for portal
              if (accessToken && token) {
                resetRefreshedTokens();
                if (JSON.parse(localStorage.getItem("is_portal_login"))) {
                  localStorageHelper.set("accessToken", accessToken);
                  localStorageHelper.set("authToken", token);
                } else {
                  //check token for AAD login for WS
                  localStorageHelper.set("wsmAccessToken", accessToken);
                  localStorageHelper.set("wsmAuthToken", token);
                }
              } else {
                logoutOnSessionExpired();
              }
            }

            // return failed request with new tokens
            return axiosInstance.request(requestData);
          } else if (
            localStorageHelper.get("auth_type") !== authTypes.azuerAD ||
            localStorageHelper.get("wsm_auth_type") !== authTypes.azuerAD
          ) {
            // fetch refresh token for OIDC user
            let tokenResponse = await getNewAccessToken();
            //Set new tokens for OIDC
            if (tokenResponse && tokenResponse?.access_token) {
              resetRefreshedTokens();
              if (JSON.parse(localStorage.getItem("is_portal_login"))) {
                localStorageHelper.set("accessToken", tokenResponse?.access_token);
              } else {
                localStorageHelper.set("wsmAccessToken", tokenResponse?.access_token);
              }
            } else {
              logoutOnSessionExpired(tokenResponse);
            }

            // return failed request with new tokens
            return axiosInstance.request(requestData);
          } else {
            // TODO: Handle error condition in refresh token fail
            toastNotification(toasterFlags.ERROR, "Something went wrong. Please try after sometime.");
          }
        } else {
          await delay(3000);
          return axiosInstance.request(requestData);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    }
    return Promise.reject(error?.response);
  }
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
